import {
  Info,
  CheckCircle,
  XCircle,
  AlertOctagon,
  Trash2,
  EyeOff,
  AlertCircle
} from 'lucide-react';

interface LogEntry {
  id: string;
  timestamp: Date;
  type: 'info' | 'success' | 'error' | 'warning';
  message: string;
  details?: any;
}

interface TransactionLogProps {
  entries: LogEntry[];
  onClear: () => void;
  onHide: () => void;
}

export default function TransactionLog({ entries, onClear, onHide }: TransactionLogProps) {
  if (entries.length === 0) return null;

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-blue-500" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Transaction Log ({entries.length})
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onClear}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors"
            title="Clear log"
          >
            <Trash2 className="h-4 w-4 text-gray-500" />
          </button>
          <button
            onClick={onHide}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors"
            title="Hide log"
          >
            <EyeOff className="h-4 w-4 text-gray-500" />
          </button>
        </div>
      </div>
      
      <div className="max-h-64 overflow-y-auto bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
        {entries.map((entry, index) => (
          <div 
            key={entry.id} 
            className={`p-3 ${index !== entries.length - 1 ? 'border-b border-gray-100 dark:border-gray-800' : ''} flex items-start gap-3 ${
              entry.type === 'error' ? 'bg-red-50 dark:bg-red-900/10' : 
              entry.type === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/10' : 
              entry.type === 'success' ? 'bg-green-50 dark:bg-green-900/10' : 
              'bg-blue-50 dark:bg-blue-900/10'
            }`}
          >
            <div className="flex-shrink-0 mt-0.5">
              {entry.type === 'info' && <Info className="h-4 w-4 text-blue-500" />}
              {entry.type === 'success' && <CheckCircle className="h-4 w-4 text-green-500" />}
              {entry.type === 'error' && <XCircle className="h-4 w-4 text-red-500" />}
              {entry.type === 'warning' && <AlertOctagon className="h-4 w-4 text-yellow-500" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-start mb-1">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {entry.message}
                </span>
                <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                  {entry.timestamp.toLocaleTimeString()}
                </span>
              </div>
              {entry.details && (
                <div className="mt-2">
                  <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded border overflow-x-auto whitespace-pre-wrap break-words">
                    {typeof entry.details === 'string' 
                      ? entry.details 
                      : JSON.stringify(entry.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
